import React, { useEffect, useState } from 'react';
import { ArrowLeft as IconLeft, ArrowRight as IconRight } from "flowbite-react-icons/outline";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from 'react-i18next';
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import Loading from "../../components/Loading";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import {getLanguage} from "../../lang/helpers";
import Api from "../../services/Api";
import {scrollTop} from "../../helpers/scrolling";

interface DriverQuestion {
  id: string
  en: string
  sv: string
  optionsType: number
  answer: number
}

interface DriverCategory {
  en: string
  sv: string
  questions: Array<DriverQuestion>
}

interface Option {
  en: string
  sv: string
  score: number
}

export default function Drivers() {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Array<DriverCategory>>([]);
  const [options, setOptions] = useState<Array<Array<Option>>>([]);
  const [driverPage, setDriverPage] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [showThanks, setShowThanks] = useState(false);
  const [lastAnswered, setLastAnswered] = useState<string | null>(null);
  const { customerCategoryId } = useParams();
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language)
  const [loaded, setLoaded] = useState<boolean>(false);
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const anonymous = !!token;

  useEffect(() => {
    let url = "client/drivers/" + customerCategoryId;
    if (token) {
      url += "/" + token;
    }
    api.get(url, [], anonymous).then((response) => {
      setDrivers(response.json.drivers);
      setOptions(response.json.options);
      setLastAnswered(response.json.lastAnswered);
      setCustomerName(response.json.customerName);
      setLoaded(true)
    });
  }, []);

  useEffect(() => {
    scrollTop();
  }, [driverPage]);

  const selectOption = (question: DriverQuestion, score: number) => {
    const _drivers = [...drivers];
    for (const _driver of _drivers) {
      for (const _question of _driver.questions) {
        if (_question.id === question.id) {
          _question.answer = score;
          console.log('Set score to ' + score);
        }
      }
    }
    setDrivers(_drivers);
  }

  if (!loaded) {
    return <Loading />;
  }

  if (drivers.length < 1) {
    return <div className="mt-4">There are no drivers mapped to this category</div>
  }

  const crumbs = [
    {name: lang === "sv" ? "Hem" : "Home", url: "/"},
    {name: t("assessments"), url: "/assessments"},
    {name: "Drivers"},
  ]

  const pageDone = () => {
    for (const driver of drivers) {
      for (const question of driver.questions) {
        if (question.answer < 0) {
          return false;
        }
      }
    }
    return true;
  }

  const driverDone = () => {
    for (const question of drivers[driverPage].questions) {
      if (question.answer < 0) {
        return false;
      }
    }
    return true;
  }

  const save = () => {
    let url = "client/drivers_save/" + customerCategoryId;
    if (token) {
      url += "/" + token;
    }
    const data = {
      drivers: drivers,
    }
    api.post(url, data, [], anonymous).then((response) => {
      setShowThanks(true);
    });
  }

  const renderOptions = (question: DriverQuestion) => {
    const questionOptions = options[question.optionsType];
    return (
      <>
        {
          questionOptions.map(option => {
            return (
              <div className="mt-2" key={"a_" + question.id + '_' + option.score}>
                <RadioButton
                  id={"a_" + question.id + '_' + option.score}
                  name={"a_" + question.id + '_' + option.score}
                  label={lang === "sv" ? option.sv : option.en}
                  value={"" + option.score}
                  checked={question.answer === option.score}
                  onChange={() => selectOption(question, option.score)}
                />
              </div>
            )
          })
        }
      </>
    )
  }

  const renderQuestions = (driver: DriverCategory) => {
    return (
      <>
        {
          driver.questions.map((question, questionNr) => {
            return (
              <div key={"question_" + question.id} className="mt-4">
                 <div className="z-title2">
                  {lang === "sv" ? question.sv : question.en}
                </div>
                {renderOptions(question)}
              </div>
            )
          })
        }
      </>
    )
  }

  const renderButtons = () => {
    const showNext = driverPage < drivers.length - 1;
    const showPrevious = driverPage > 0 && drivers.length > 1;
    const showSave = driverPage === drivers.length - 1;
    return (
      <div className="flex justify-between mt-6 gap-x-8">

        {
          showPrevious &&
            <CustomButton onClick={() => setDriverPage(driverPage - 1)}
                          leftIcon={<IconLeft className="size-4 text-neutral-900!" aria-hidden="true" />}>
              {t('back')}
            </CustomButton>
        }
        {
          showNext &&
            <CustomButton disabled={!driverDone()} onClick={() => setDriverPage(driverPage + 1)}
                          rightIcon={<IconRight className="size-4 text-neutral-900!" aria-hidden="true" />}>
              {t('next')}
            </CustomButton>
        }
        {
          showSave &&
            <CustomButton disabled={!pageDone()} onClick={() => save()}
                          rightIcon={<IconRight className="size-4 text-neutral-900!" aria-hidden="true" />}>
              {t('submit')}
            </CustomButton>
        }

      </div>
    )
  }

  const changeLanguage = (l: string)  => {
    if (l === 'en' || l === 'sv') {
      i18n.changeLanguage(l).then();
    }
  }

  const renderIntro = () => {
    let intro = "";
    let disclaimer = "";
    if (lang === 'sv') {
      intro += "Ange i vilken grad du instämmer i påståendena nedan, utifrån hur det ser ut i ditt arbete just nu. Det finns inga rätta eller felaktiga svar, det är din upplevelse som räknas."

      disclaimer += "Ingen på " + customerName + " kan se vem som svarat vad."
    } else {
      intro += "Rate how much you agree with the statements below, based on how things look in your work right now. There are no right or wrong answers, it’s your experience that counts.";
      disclaimer += "No one at " + customerName + " can see who answered what."
    }


    return (
      <>
        <p className="my-2 z-body1">{intro}</p>
        <p className="my-2 z-body1">{disclaimer}</p>
      </>
    )
  }

  const thisDriver = drivers[driverPage];

  if (showThanks) {
    return (
      <div className="main-content">
        {
          !anonymous &&
            <LocalBreadcrumbs crumbs={crumbs}/>
        }
        <p className="z-h2 mt-4">{lang === "sv" ? "Tack för dina svar!" : "Thanks for your answers!"}</p>
        {
          !anonymous &&
            <CustomButton buttonType="blueBase" className="mt-4"
                          onClick={() => navigate("/assessments")}>
            {lang === "sv" ? "Tillbaka till mätningar" : "Back to assessments"}
          </CustomButton>
        }
      </div>
    )
  }

  return (
    <div className="main-content">
      {
        anonymous ? (
          <div className="flex gap-x-4 mt-4 justify-end">
            <div className={lang === "en" ? "underline" : "cursor-pointer"} onClick={() => changeLanguage("en")}>
              English
            </div>
            <div className={lang === "sv" ? "underline" : "cursor-pointer"} onClick={() => changeLanguage("sv")}>
              Svenska
            </div>
          </div>
          ) : (
          <LocalBreadcrumbs crumbs={crumbs}/>
        )

      }

      {renderIntro()}

      {/*{*/}
      {/*  lastAnswered &&*/}
      {/*  <p className="mt-2 z-body3">Prefilling answers from {lastAnswered.substring(0, 10)}*/}
      {/*  </p>*/}
      {/*}*/}

      <p className="z-body2 mt-4">Step {driverPage + 1} of {drivers.length}</p>
      <div className="mt-2 w-full h-2 rounded-full bg-indigo-700">
        <div className="rounded-full h-2 bg-primary-500" style={{width: (100 * (driverPage + 1) / drivers.length) + "%"}}/>
      </div>

      <div className="mt-6">
        <div className="z-h2">
          {lang === "sv" ? thisDriver.sv : thisDriver.en}
        </div>
        {renderQuestions(thisDriver)}

        <hr className="my-8 border-indigo-700"/>
      </div>
      {renderButtons()}
    </div>
  )
}
